import React, { useState, useEffect } from "react"
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Image,
  AspectRatio,
  Stack,
  IconButton,
  Grid,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  VStack,
  Badge,
  Spacer,
  HStack,
  Alert,
  AlertIcon,
  Spinner,
} from "@chakra-ui/react"
import { Formik, Form, Field } from "formik"
import * as Yup from "yup"
import axios from "axios"
import { Edit, Trash2, Plus, Upload, Info } from "lucide-react"

import { Space, Table, Tag } from "antd"
import Calendar from "react-calendar"
import "react-calendar/dist/Calendar.css"
import { format } from "date-fns" // optional, for clean date formatting

const API_BASE = `${process.env.REACT_APP_BASE_URL}/admin`
const IMAGE_BASE = process.env.REACT_APP_IMAGE_BASE_URL || ""

type NotificationType = "BIRTHDAY" | "ANNIVERSARY" | "RELATIVE BIRTHDAY"

// Validation schema
const celebrationSchema = Yup.object().shape({
  message: Yup.string().required("Message is required").trim(),
  notificationType: Yup.string()
    .oneOf(["BIRTHDAY", "ANNIVERSARY", "RELATIVE BIRTHDAY"])
    .required("Type is required"),
  image: Yup.mixed().required("Image is required"),
})

// Single Celebration Card
const CelebrationCard = ({ celebration, onEdit, onDelete }: any) => {
  const imageUrl = celebration.image?.url
    ? `${IMAGE_BASE}/${celebration.image.url}`
    : typeof celebration.image === "string"
    ? celebration.image
    : ""

  const getBadgeColor = (type: string) => {
    switch (type) {
      case "BIRTHDAY":
        return "pink"
      case "ANNIVERSARY":
        return "purple"
      case "RELATIVE BIRTHDAY":
        return "teal"
      default:
        return "gray"
    }
  }

  return (
    <Box
      borderWidth="1px"
      borderRadius="xl"
      overflow="hidden"
      bg="white"
      boxShadow="md"
      position="relative"
      transition="all 0.2s"
      _hover={{ boxShadow: "xl", transform: "translateY(-6px)" }}
    >
      <AspectRatio ratio={16 / 9}>
        <Image
          src={imageUrl || "https://via.placeholder.com/800x450?text=No+Image"}
          alt={celebration.notificationType}
          objectFit="cover"
          fallbackSrc="https://via.placeholder.com/800x450?text=Loading..."
        />
      </AspectRatio>

      <Box p={5}>
        <Badge
          colorScheme={getBadgeColor(celebration.notificationType)}
          mb={3}
          px={4}
          py={1}
          borderRadius="full"
          fontSize="sm"
        >
          {celebration.notificationType}
        </Badge>
        <Text fontWeight="semibold" fontSize="lg" noOfLines={4}>
          {celebration.message}
        </Text>
      </Box>

      <VStack position="absolute" top={3} right={3} spacing={2}>
        <IconButton
          size="sm"
          colorScheme="blue"
          aria-label="Edit"
          icon={<Edit size={16} />}
          onClick={onEdit}
          borderRadius="full"
          boxShadow="md"
        />
        <IconButton
          size="sm"
          colorScheme="red"
          aria-label="Delete"
          icon={<Trash2 size={16} />}
          onClick={onDelete}
          borderRadius="full"
          boxShadow="md"
        />
      </VStack>
    </Box>
  )
}

/* -------------------- COMPONENT -------------------- */

/* -------------------- COMPONENT -------------------- */

const CelebrationFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  toast,
}: {
  isOpen: boolean
  onClose: () => void
  onSubmit: (values: any) => void
  initialValues?: any
  toast: any
}) => {
  const [imagePreview, setImagePreview] = useState("")
  const [uploadingImage, setUploadingImage] = useState(false)

  /* -------------------- PRELOAD IMAGE -------------------- */

  useEffect(() => {
    if (initialValues?.image?.url) {
      setImagePreview(`${IMAGE_BASE}/${initialValues.image.url}`)
    } else {
      setImagePreview("")
    }
  }, [initialValues])

  /* -------------------- IMAGE UPLOAD -------------------- */

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: any,
    setFieldTouched: any
  ) => {
    const file = e.target.files?.[0]
    if (!file) return

    e.target.value = ""

    setUploadingImage(true)
    setFieldTouched("image", true)

    try {
      const formData = new FormData()
      formData.append("file", file)

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/upload/media`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      )

      const mediaId = res.data.data._id
      const previewUrl = `${IMAGE_BASE}/${res.data.data.url}`

      setImagePreview(previewUrl)
      setFieldValue("image", mediaId)

      toast({
        title: "Image uploaded",
        status: "success",
        duration: 3000,
      })
    } catch (err: any) {
      setFieldValue("image", "")
      toast({
        title: "Image upload failed",
        description: err.response?.data?.message || "Please try again",
        status: "error",
        duration: 5000,
      })
    } finally {
      setUploadingImage(false)
    }
  }

  /* -------------------- PLACEHOLDER LOGIC -------------------- */

  const getPlaceholderMeta = (type: NotificationType, message: string) => {
    if (type === "BIRTHDAY") {
      return {
        required: true,
        valid: message.includes("${name}"),
        text: "Placeholder ${name} is required",
        placeholder: "e.g. Happy Birthday ${name}! Wishing you a great day 🎉",
      }
    }

    if (type === "RELATIVE BIRTHDAY") {
      return {
        required: true,
        valid: message.includes("${relation}"),
        text: "Placeholder ${relation} is required",
        placeholder: "e.g. Happy Birthday to your ${relation}! 🎂",
      }
    }

    return {
      required: false,
      valid: true,
      text: "",
      placeholder: "e.g. Happy Anniversary! Wishing you both endless love ❤️",
    }
  }

  /* -------------------- RENDER -------------------- */

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent borderRadius="2xl" maxW="700px">
        <ModalHeader bg="purple.50">
          {initialValues ? "Edit Celebration Banner" : "Add Celebration Banner"}
        </ModalHeader>
        <ModalCloseButton />

        <Formik
          initialValues={{
            message: initialValues?.message || "",
            notificationType: initialValues?.notificationType || "BIRTHDAY",
            image: initialValues?.image?._id || "",
          }}
          validationSchema={celebrationSchema}
          enableReinitialize
          onSubmit={(values, { setSubmitting }) => {
            onSubmit(values)
            setSubmitting(false)
            onClose()
          }}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
          }: any) => {
            const placeholderMeta = getPlaceholderMeta(
              values.notificationType as NotificationType,
              values.message
            )

            const showPlaceholderError =
              placeholderMeta.required && !placeholderMeta.valid

            return (
              <Form>
                <ModalBody py={8}>
                  <Stack spacing={6}>
                    {/* TYPE */}
                    <FormControl>
                      <FormLabel>Notification Type</FormLabel>
                      <Field as={Select} name="notificationType">
                        <option value="BIRTHDAY">Birthday</option>
                        <option value="RELATIVE BIRTHDAY">
                          Relative Birthday
                        </option>
                        <option value="ANNIVERSARY">Anniversary</option>
                      </Field>
                    </FormControl>

                    {/* PLACEHOLDER WARNING */}
                    {placeholderMeta.required && (
                      <Alert
                        status={showPlaceholderError ? "warning" : "info"}
                        borderRadius="lg"
                      >
                        <AlertIcon />
                        <Text>
                          {showPlaceholderError
                            ? placeholderMeta.text
                            : "Placeholder requirement satisfied"}
                        </Text>
                      </Alert>
                    )}

                    {/* MESSAGE */}
                    <FormControl
                      isInvalid={
                        (!!errors.message && !!touched.message) ||
                        showPlaceholderError
                      }
                    >
                      <FormLabel>Message</FormLabel>
                      <Field
                        as={Textarea}
                        name="message"
                        rows={5}
                        placeholder={placeholderMeta.placeholder}
                        fontFamily="mono"
                        borderColor={
                          showPlaceholderError ? "red.400" : undefined
                        }
                        _focus={{
                          borderColor: showPlaceholderError
                            ? "red.500"
                            : "purple.400",
                        }}
                      />

                      {errors.message && touched.message && (
                        <Text color="red.500" fontSize="sm">
                          {errors.message}
                        </Text>
                      )}

                      {showPlaceholderError && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {placeholderMeta.text}
                        </Text>
                      )}
                    </FormControl>

                    {/* IMAGE */}
                    <FormControl isInvalid={!!errors.image && !!touched.image}>
                      <FormLabel>Banner Image (16:9)</FormLabel>

                      <Box
                        border="2px dashed"
                        borderColor="gray.300"
                        borderRadius="xl"
                        p={8}
                        textAlign="center"
                        cursor="pointer"
                        _hover={{
                          borderColor: "purple.400",
                          bg: "purple.50",
                        }}
                        onClick={() =>
                          document.getElementById("celebration-image")?.click()
                        }
                      >
                        <VStack spacing={3}>
                          {uploadingImage ? (
                            <>
                              <Spinner color="purple.500" />
                              <Text>Uploading image...</Text>
                            </>
                          ) : (
                            <>
                              <Upload size={32} />
                              <Text>Click to upload image</Text>
                              <Text fontSize="sm" color="gray.500">
                                JPG / PNG • Max 5MB
                              </Text>
                            </>
                          )}
                        </VStack>
                      </Box>

                      <Input
                        id="celebration-image"
                        type="file"
                        accept="image/*"
                        display="none"
                        disabled={uploadingImage}
                        onChange={(e) =>
                          handleImageUpload(e, setFieldValue, setFieldTouched)
                        }
                      />

                      {errors.image && touched.image && (
                        <Text color="red.500" fontSize="sm" mt={2}>
                          {errors.image}
                        </Text>
                      )}

                      {imagePreview && (
                        <Box mt={5} borderRadius="lg" overflow="hidden">
                          <AspectRatio ratio={16 / 9}>
                            <Image src={imagePreview} objectFit="cover" />
                          </AspectRatio>
                        </Box>
                      )}
                    </FormControl>
                  </Stack>
                </ModalBody>

                <ModalFooter>
                  <Button variant="ghost" mr={4} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="purple"
                    type="submit"
                    isLoading={isSubmitting || uploadingImage}
                    isDisabled={
                      uploadingImage || isSubmitting || showPlaceholderError
                    }
                  >
                    {initialValues ? "Update Banner" : "Create Banner"}
                  </Button>
                </ModalFooter>
              </Form>
            )
          }}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

// Main Page
const HTCelebrationNotificationsPage = () => {
  const [celebrations, setCelebrations] = useState<any[]>([])
  const [filteredCelebrations, setFilteredCelebrations] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filterType, setFilterType] = useState<string>("ALL")
  const [editingItem, setEditingItem] = useState<any>(null)

  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()

  useEffect(() => {
    fetchCelebrations()
  }, [])

  useEffect(() => {
    if (filterType === "ALL") {
      setFilteredCelebrations(celebrations)
    } else {
      setFilteredCelebrations(
        celebrations.filter((c) => c.notificationType === filterType)
      )
    }
  }, [celebrations, filterType])

  const fetchCelebrations = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${API_BASE}/notification-ht-celebration`)
      setCelebrations(res.data.data || [])
    } catch (err: any) {
      toast({
        title: "Failed to load data",
        description: err.response?.data?.message || "Please try again later",
        status: "error",
        duration: 5000,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingItem(null)
    onOpen()
  }

  const handleEdit = (item: any) => {
    setEditingItem(item)
    onOpen()
  }

  const handleDelete = async (id: string) => {
    if (
      !window.confirm(
        "Delete this celebration banner? This action cannot be undone."
      )
    )
      return

    try {
      await axios.delete(`${API_BASE}/notification-ht-celebration/${id}`)
      toast({
        title: "Deleted successfully",
        status: "success",
        duration: 3000,
      })
      fetchCelebrations()
    } catch (err: any) {
      toast({
        title: "Delete failed",
        description: err.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 5000,
      })
    }
  }

  const handleSubmit = async (values: any) => {
    try {
      if (editingItem) {
        await axios.patch(
          `${API_BASE}/notification-ht-celebration/${editingItem._id}`,
          values
        )
        toast({
          title: "Updated successfully",
          status: "success",
          duration: 3000,
        })
      } else {
        await axios.post(`${API_BASE}/notification-ht-celebration`, values)
        toast({
          title: "Created successfully",
          status: "success",
          duration: 3000,
        })
      }
      fetchCelebrations()
    } catch (err: any) {
      toast({
        title: "Operation failed",
        description: err.response?.data?.message || "Please try again",
        status: "error",
        duration: 5000,
      })
    }
  }

  return (
    <>
      {/* Header */}
      <VStack position="relative" top={5} mb={8}>
        <Flex w="92%" align="center" justify="space-between">
          <Heading ml={8} size="md" fontWeight="semibold" color="gray.700">
            HT Celebration Notifications
          </Heading>
          <Spacer />
          <Flex align="center" gap={3}>
            <Image
              borderRadius="full"
              boxSize="50px"
              src="https://bit.ly/dan-abramov"
              alt="Admin"
            />
            <Text fontWeight="medium">Admin</Text>
          </Flex>
        </Flex>
      </VStack>

      {/* Main Content */}
      <div
        style={{
          width: "96vw",
          maxWidth: "96vw",
          height: "86vh",
          overflowY: "auto",
          backgroundColor: "#EEEEEE",
          padding: "30px",
          position: "relative",
          top: "8vh",
          left: "4vw",
        }}
      >
        <Box maxW="1440px" mx="auto">
          <Box
            bg="white"
            borderRadius="2xl"
            boxShadow="xl"
            p={8}
            borderTop="6px solid #9F7AEA"
          >
            <Flex justify="space-between" align="center" mb={8}>
              <VStack align="start" spacing={1}>
                <Heading size="xl">Celebration Notification Template</Heading>
                <Text color="gray.600" fontSize="lg">
                  Create birthday, relative birthday & anniversary celebration
                </Text>
              </VStack>

              <HStack spacing={6}>
                {/* Filter */}
                <FormControl maxW="260px">
                  <FormLabel fontSize="sm" fontWeight="medium" mb={1}>
                    Filter by Type
                  </FormLabel>
                  <Select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    bg="white"
                    borderColor="gray.300"
                  >
                    <option value="ALL">All Types</option>
                    <option value="BIRTHDAY">Birthday</option>
                    <option value="RELATIVE BIRTHDAY">Relative Birthday</option>
                    <option value="ANNIVERSARY">Anniversary</option>
                  </Select>
                </FormControl>

                <Button
                  leftIcon={<Plus size={20} />}
                  colorScheme="purple"
                  size="lg"
                  px={8}
                  borderRadius="full"
                  fontWeight="semibold"
                  onClick={handleAdd}
                >
                  Add New
                </Button>
              </HStack>
            </Flex>

            {loading ? (
              <Flex h="400px" align="center" justify="center">
                <Text fontSize="lg" color="gray.500">
                  Loading banners...
                </Text>
              </Flex>
            ) : filteredCelebrations.length === 0 ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                h="500px"
                bg="gray.50"
                borderRadius="2xl"
                border="3px dashed"
                borderColor="gray.300"
              >
                <Text fontSize="xl" fontWeight="medium" color="gray.600" mb={4}>
                  {filterType === "ALL"
                    ? "No celebration banners yet"
                    : `No ${filterType.toLowerCase()} banners found`}
                </Text>
                <Text color="gray.500" textAlign="center" maxW="500px">
                  {filterType === "ALL"
                    ? "Click 'Add New Banner' to create your first celebration banner"
                    : "Try changing the filter or create a new one"}
                </Text>
              </Flex>
            ) : (
              <Grid
                templateColumns="repeat(auto-fill, minmax(360px, 1fr))"
                gap={8}
              >
                {filteredCelebrations.map((item) => (
                  <CelebrationCard
                    key={item._id}
                    celebration={item}
                    onEdit={() => handleEdit(item)}
                    onDelete={() => handleDelete(item._id)}
                  />
                ))}
              </Grid>
            )}
          </Box>
        </Box>

        <HTCelebrationCalendarTable />
      </div>

      {/* Modal */}
      <CelebrationFormModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleSubmit}
        initialValues={editingItem}
        toast={toast}
      />
    </>
  )
}

interface Notification {
  _id: string
  user: {
    firstName: string
    lastName: string
  }
  notificationType: "BIRTHDAY" | "ANNIVERSARY" | "RELATIVE BIRTHDAY"
  createdAt: string // ISO date string
}

const HTCelebrationCalendarTable: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ])
  const [startDate, endDate] = dateRange
  const [data, setData] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)

  // Fetch data whenever date range changes
  useEffect(() => {
    if (startDate && endDate) {
      fetchNotificationsInRange(startDate, endDate)
    } else {
      setData([]) // clear if no range selected
    }
  }, [startDate, endDate])

  const fetchNotificationsInRange = async (start: Date, end: Date) => {
    setLoading(true)
    try {
      const formattedStart = format(start, "yyyy-MM-dd")
      const formattedEnd = format(end, "yyyy-MM-dd")

      const res = await axios.get(
        `${API_BASE}/notification-ht-celebration/sent`,
        {
          params: {
            startDate: formattedStart,
            endDate: formattedEnd,
          },
        }
      )

      // Adjust this based on your actual API response structure
      setData(res.data.data || [])
    } catch (err: any) {
      console.error("Failed to fetch notifications:", err)
      setData([])
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      title: "No.",
      key: "index",
      render: (_: any, __: any, index: number) => index + 1,
      width: 80,
    },
    {
      title: "User Name",
      key: "user",
      render: (record: Notification) =>
        `${record.user.firstName} ${record.user.lastName}`,
      sorter: (a: Notification, b: Notification) =>
        `${a.user.firstName} ${a.user.lastName}`.localeCompare(
          `${b.user.firstName} ${b.user.lastName}`
        ),
    },
    {
      title: "Notification Type",
      dataIndex: "notificationType",
      key: "notificationType",
      render: (type: string) => {
        let color = "purple"
        if (type === "BIRTHDAY") color = "pink"
        if (type === "RELATIVE BIRTHDAY") color = "teal"
        if (type === "ANNIVERSARY") color = "purple"

        return <Tag color={color}>{type}</Tag>
      },
      filters: [
        { text: "Birthday", value: "BIRTHDAY" },
        { text: "Relative Birthday", value: "RELATIVE BIRTHDAY" },
        { text: "Anniversary", value: "ANNIVERSARY" },
      ],
      onFilter: (value: any, record: Notification) =>
        record.notificationType === value,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "date",
      render: (date: string) => format(new Date(date), "dd MMM yyyy"),
      sorter: (a: Notification, b: Notification) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
  ]

  return (
    <div
      style={{
        width: "96vw",
        maxWidth: "96vw",
        height: "86vh",
        overflowY: "auto",
        backgroundColor: "#EEEEEE",
        padding: "30px",
        position: "relative",
        top: "8vh",
        left: "4vw",
      }}
    >
      <Box maxW="1440px" mx="auto">
        <Heading size="lg" mb={6} color="gray.700">
          Celebration Notifications
        </Heading>

        <Flex gap={8} flexWrap="wrap">
          {/* Calendar Section */}
          <Box
            bg="white"
            p={6}
            borderRadius="2xl"
            boxShadow="xl"
            flex="0 0 380px"
            borderTop="5px solid #9F7AEA"
          >
            <Text fontSize="lg" fontWeight="semibold" mb={4}>
              Select Date Range
            </Text>

            <Calendar
              onChange={(value: any) => setDateRange(value as [Date, Date])}
              maxDate={new Date()}
              value={dateRange}
              selectRange={true}
              locale="en-US"
              className="custom-calendar"
            />

            {startDate && endDate && (
              <Box mt={4} p={3} bg="purple.50" borderRadius="lg">
                <Text fontSize="sm" fontWeight="medium">
                  Selected Range:
                </Text>
                <Text fontSize="md" color="purple.700">
                  {format(startDate, "dd MMM yyyy")} —{" "}
                  {format(endDate, "dd MMM yyyy")}
                </Text>
              </Box>
            )}

            {!startDate || !endDate ? (
              <Text mt={4} color="gray.500" fontStyle="italic">
                Click two dates to select a range
              </Text>
            ) : null}
          </Box>

          {/* Table Section */}
          <Box flex="1" minW="600px">
            <Box bg="white" borderRadius="2xl" boxShadow="xl" p={6}>
              <Flex justify="space-between" align="center" mb={4}>
                <Text fontSize="lg" fontWeight="semibold">
                  Notifications ({data.length})
                </Text>
                {startDate && endDate && (
                  <Text fontSize="sm" color="gray.600">
                    Showing results for selected date range
                  </Text>
                )}
              </Flex>

              <Table
                columns={columns}
                dataSource={data}
                rowKey="_id"
                loading={loading}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                }}
                scroll={{ x: 800 }}
                locale={{ emptyText: "Nootifications in selected range" }}
              />
            </Box>
          </Box>
        </Flex>
      </Box>
    </div>
  )
}

export default HTCelebrationNotificationsPage
